/**
 * Reward Service
 * 
 * Abstracts data fetching for reward catalog. Uses real API calls.
 * 
 * Follows Interface Segregation and Dependency Inversion principles.
 */

import type {
  RewardResponse,
  RewardListResponse,
  RewardQueryParams,
  CreateRewardRequest,
  UpdateRewardRequest,
  RewardStatus,
  PoolListResponse,
} from '@/app/types/rewards';
import { api } from '@/app/services/apiClient';
import { API_ENDPOINTS } from '@/app/config/api';

/**
 * Map backend status to frontend status terminology
 * Backend uses: DRAFT, ACTIVE, INACTIVE
 * Frontend uses: DRAFT, ACTIVE, ARCHIVED
 */
function mapBackendStatus(status: string): RewardStatus {
  return status === 'INACTIVE' ? 'ARCHIVED' : status as RewardStatus;
}

/**
 * Map response status from backend to frontend terminology
 */
function mapRewardResponse(reward: RewardResponse): RewardResponse {
  return {
    ...reward,
    status: mapBackendStatus(reward.status),
  };
}

/**
 * Reward metadata response
 */
export interface RewardMetadataResponse {
  categories: string[];
  variants: string[];
  reward_keys: string[];
}

/**
 * Reward Service Interface
 * Define the contract that any implementation must follow
 */
export interface IRewardService {
  queryRewards(params: RewardQueryParams): Promise<RewardListResponse>;
  getRewardById(id: string): Promise<RewardResponse | null>;
  createReward(request: CreateRewardRequest): Promise<RewardResponse>;
  updateReward(id: string, request: UpdateRewardRequest): Promise<RewardResponse | null>;
  archiveReward(id: string): Promise<RewardResponse | null>;
  restoreReward(id: string): Promise<RewardResponse | null>;
  getMetadata(): Promise<RewardMetadataResponse>;
  getPools(): Promise<PoolListResponse>;
}

/**
 * Real API Implementation
 */
const apiRewardService: IRewardService = {
  async queryRewards(params: RewardQueryParams): Promise<RewardListResponse> {
    try {
      // Build query params, filtering out undefined/null values
      // Note: Backend uses 'reward_status' as query param name, not 'status'
      // Also, backend uses 'INACTIVE' but frontend uses 'ARCHIVED' - map ARCHIVED -> INACTIVE
      const queryParams: Record<string, any> = {};
      if (params.category !== undefined && params.category !== null) {
        queryParams.category = params.category;
      }
      if (params.status !== undefined && params.status !== null) {
        // Map frontend 'ARCHIVED' to backend 'INACTIVE'
        const backendStatus = params.status === 'ARCHIVED' ? 'INACTIVE' : params.status;
        queryParams.reward_status = backendStatus; // Map 'status' to 'reward_status' for backend
      }
      if (params.reward_variant !== undefined && params.reward_variant !== null) {
        queryParams.reward_variant = params.reward_variant;
      }
      if (params.search !== undefined && params.search !== null) {
        queryParams.search = params.search;
      }
      if (params.limit !== undefined && params.limit !== null) {
        queryParams.limit = params.limit;
      }
      if (params.offset !== undefined && params.offset !== null) {
        queryParams.offset = params.offset;
      }

      const response = await api.get<RewardListResponse>(API_ENDPOINTS.admin.rewards, {
        params: queryParams,
      });
      // Map backend status to frontend status for all rewards
      return {
        ...response.data,
        rewards: response.data.rewards.map(mapRewardResponse),
      };
    } catch (error: any) {
      console.error('Failed to query rewards:', error);
      throw error;
    }
  },

  async getRewardById(id: string): Promise<RewardResponse | null> {
    try {
      const response = await api.get<RewardResponse>(
        `${API_ENDPOINTS.admin.rewards}/${id}`
      );
      return mapRewardResponse(response.data);
    } catch (error: any) {
      if (error.response?.status === 404) {
        return null;
      }
      console.error('Failed to get reward:', error);
      throw error;
    }
  },

  async createReward(request: CreateRewardRequest): Promise<RewardResponse> {
    try {
      const response = await api.post<RewardResponse>(API_ENDPOINTS.admin.rewards, request);
      return mapRewardResponse(response.data);
    } catch (error: any) {
      console.error('Failed to create reward:', error);
      throw error;
    }
  },

  async updateReward(id: string, request: UpdateRewardRequest): Promise<RewardResponse | null> {
    try {
      // Map frontend 'ARCHIVED' to backend 'INACTIVE' for status field
      const mappedRequest = { ...request };
      if (mappedRequest.status === 'ARCHIVED') {
        mappedRequest.status = 'INACTIVE' as any;
      }
      
      const response = await api.patch<RewardResponse>(
        `${API_ENDPOINTS.admin.rewards}/${id}`,
        mappedRequest
      );
      return mapRewardResponse(response.data);
    } catch (error: any) {
      if (error.response?.status === 404) {
        return null;
      }
      console.error('Failed to update reward:', error);
      throw error;
    }
  },

  async archiveReward(id: string): Promise<RewardResponse | null> {
    // Archive = update status to ARCHIVED (mapped to INACTIVE for backend)
    return this.updateReward(id, { status: 'ARCHIVED' });
  },

  async restoreReward(id: string): Promise<RewardResponse | null> {
    // Restore = update status to ACTIVE
    return this.updateReward(id, { status: 'ACTIVE' });
  },

  async getMetadata(): Promise<RewardMetadataResponse> {
    try {
      const response = await api.get<RewardMetadataResponse>(API_ENDPOINTS.admin.rewardMetadata);
      return response.data;
    } catch (error: any) {
      console.error('Failed to get reward metadata:', error);
      throw error;
    }
  },

  async getPools(): Promise<PoolListResponse> {
    try {
      const response = await api.get<PoolListResponse>(API_ENDPOINTS.admin.rewardPools);
      return response.data;
    } catch (error: any) {
      console.error('Failed to get pools:', error);
      return { annual_pools: [], monthly_pools: [] };
    }
  },
};

// Export the active implementation
export const rewardService: IRewardService = apiRewardService;

// Also export as default for easier imports
export default rewardService;

